import { useState, useEffect } from 'react';
import LayoutGrid from './LayoutGrid';
import ChartPanel from './ChartPanel';
import {
  Box,
  Autocomplete,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Favorite as FavoriteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { tokens } from '../../../theme';
import { useTheme } from '@mui/material/styles';
import { useIsMobile } from '../../../components/hooks';
import { useFetchingData } from '../../../components/api.js';

// ─── FavoriteListPopup for 4×3 ───────────────────────────────────────────
const FavoriteListPopup43 = ({
  selectedFavorite,
  setSelectedFavorite,
  symbolSelected,
  applyFavoriteSymbols,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useIsMobile();

  const [favorites, setFavorites] = useState([]);
  const [openSettings, setOpenSettings] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);
  const [editedFavoriteName, setEditedFavoriteName] = useState('');
  const [editedSymbols, setEditedSymbols] = useState([]);

  // 1) Load favorites from localStorage on mount
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('favoriteLists43')) || [];
    setFavorites(stored);
  }, []);

  // 2) Whenever favorites array changes, persist back to localStorage
  useEffect(() => {
    localStorage.setItem('favoriteLists43', JSON.stringify(favorites));
  }, [favorites]);

  // 3) Keep edited state in sync
  useEffect(() => {
    if (selectedFavorite) {
      setEditedFavoriteName(selectedFavorite.name);
      setEditedSymbols([...selectedFavorite.symbols]);
    }
  }, [selectedFavorite]);

  const handleFavoriteSelect = (event, value) => {
    if (!value) {
      setSelectedFavorite(null);
      applyFavoriteSymbols([]);
    } else {
      setSelectedFavorite(value);
      applyFavoriteSymbols(value.symbols);
    }
  };

  const addOrUpdateFavorite = () => {
    if (selectedFavorite) {
      const updated = favorites.map((fav) =>
        fav.name === selectedFavorite.name
          ? { name: editedFavoriteName || fav.name, symbols: [...symbolSelected] }
          : fav
      );
      setFavorites(updated);
      const newly = updated.find((f) => f.name === (editedFavoriteName || selectedFavorite.name));
      setSelectedFavorite(newly || null);
    } else {
      const nextName = `Favorite ${favorites.length + 1}`;
      const newFav = { name: nextName, symbols: [...symbolSelected] };
      setFavorites([...favorites, newFav]);
      setSelectedFavorite(null);
    }
    setEditedFavoriteName('');
    setEditedSymbols([]);
  };

  const handleOpenSettings = (fav) => {
    setSelectedFavorite(fav);
    setEditedFavoriteName(fav.name);
    setEditedSymbols(fav.symbols);
    setOpenSettings(true);
  };

  const handleSaveSettings = () => {
    const updated = favorites.map((fav) =>
      fav.name === selectedFavorite.name
        ? { name: editedFavoriteName, symbols: [...editedSymbols] }
        : fav
    );
    setFavorites(updated);
    const newly = updated.find((f) => f.name === editedFavoriteName);
    setSelectedFavorite(newly || null);
    setOpenSettings(false);
  };

  const handleOpenDeleteConfirm = (fav) => {
    setOptionToDelete(fav);
    setOpenDeleteConfirm(true);
  };

  const handleDeleteFavorite = () => {
    const filtered = favorites.filter((fav) => fav.name !== optionToDelete.name);
    setFavorites(filtered);
    if (selectedFavorite?.name === optionToDelete.name) {
      setSelectedFavorite(null);
      applyFavoriteSymbols([]);
    }
    setOpenDeleteConfirm(false);
  };

  return (
    <>
      {/* Container สำหรับแถบ Favorites ด้านบน */}
      <Box
        sx={{
          position: 'fixed',
          top: isMobile ? 8 : 16,
          right: isMobile ? 8 : 24,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1.5,
          zIndex: 1200,
        }}
      >
        <Autocomplete
          disablePortal
          id="favoriteList43"
          size="small"
          options={favorites}
          value={selectedFavorite}
          getOptionLabel={(option) => option.name}
          onChange={handleFavoriteSelect}
          renderOption={(props, option) => (
            <li
              {...props}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#0f172a',
                color: 'white',
                borderBottom: '1px solid #1e293b'
              }}
            >
              <span>{option.name}</span>
              <Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenSettings(option); }}>
                  <EditIcon fontSize="small" sx={{ color: 'white' }} />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDeleteConfirm(option); }}>
                  <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
                </IconButton>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Favorites"
              sx={{
                backgroundColor: '#111827',
                borderRadius: '0.5rem',
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': { borderColor: '#334155' },
                  '&:hover fieldset': { borderColor: '#475569' },
                  '&.Mui-focused fieldset': { borderColor: '#06b6d4' },
                },
                '& .MuiSvgIcon-root': { color: '#94a3b8' }
              }}
            />
          )}
          sx={{ width: isMobile ? 140 : 220 }}
        />

        <Button
          variant="contained"
          startIcon={selectedFavorite ? <SaveIcon /> : <FavoriteIcon />}
          onClick={addOrUpdateFavorite}
          sx={{
            bgcolor: selectedFavorite ? '#06b6d4' : '#ef4444', 
            color: '#fff',
            textTransform: 'none',
            fontWeight: 'bold',
            height: '40px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            '&:hover': { bgcolor: selectedFavorite ? '#0891b2' : '#dc2626' },
          }}
        >
          {selectedFavorite ? 'UPDATE' : 'ADD'}
        </Button>
      </Box>

      {/* Dialogs สำหรับการแก้ไขและลบ */}
      <Dialog open={openSettings} onClose={() => setOpenSettings(false)}>
        <DialogTitle>Edit Favorite</DialogTitle>
        <DialogContent>
          <TextField label="Name" fullWidth value={editedFavoriteName} onChange={(e) => setEditedFavoriteName(e.target.value)} sx={{ mt: 1 }} />
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            This will overwrite the current symbols (in all twelve charts) under that name.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSettings(false)}>Cancel</Button>
          <Button onClick={handleSaveSettings} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>Delete Favorite</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{optionToDelete?.name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={handleDeleteFavorite} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── Layout43 (4×3) ─────────────────────────────────────────────────
export default function Layout43() {
  const { __fetching_data__ } = useFetchingData();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [symbols, setSymbols] = useState(Array(12).fill(null));
  const [options, setOptions] = useState([]);
  const [selectedFavorite, setSelectedFavorite] = useState(null);

  const baseApiUrl = process.env.NODE_ENV === 'production'
      ? (window.location.protocol === 'https:' ? process.env.REACT_APP_API_HTTPS_URL : process.env.REACT_APP_API_HTTP_URL)
      : '/application';

  useEffect(() => {
    __fetching_data__({ url:`${baseApiUrl}/realtime_symbols` })
      .then(r => setOptions(r.data.symbols))
      .catch(console.error);
  }, []);  

  const applyFavoriteSymbols = (favArray) => {
    const arr = Array(12).fill(null).map((_, idx) => favArray[idx] || null);
    setSymbols(arr);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <FavoriteListPopup43
        selectedFavorite={selectedFavorite}
        setSelectedFavorite={setSelectedFavorite}
        symbolSelected={symbols}
        applyFavoriteSymbols={applyFavoriteSymbols}
      />

      <LayoutGrid columns={4} gap={1}>
        {symbols.map((sym, idx) => (
          <ChartPanel
            key={idx}
            symbol={sym}
            symbolsList={options}
            baseApiUrl={baseApiUrl}
            onSymbolChange={(v) =>
              setSymbols((prev) => {
                const copy = [...prev];
                copy[idx] = v;
                return copy;
              })
            }
          />
        ))}
      </LayoutGrid>
    </Box>
  );
}